import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { AccessDenied } from '@/pages/AccessDenied'
import { ApiError } from '@/lib/api'
import { blankToNull, CreateField, CreateModal } from './CreateModal'
import { createLead, type NewLead } from './leadApi'

const SOURCES = ['call', 'email', 'website', 'telegram', 'referral', 'exhibition', 'other'] as const

/**
 * Заведение лида вручную (тикет #101): звонок, письмо, визитка с выставки. Вёрстка по
 * CreateRecord.dc.html (форма `lead`).
 *
 * Сервер требует хотя бы один контакт — почту, телефон или ИНН: обращение без зацепки
 * невозможно отработать. Кнопка гаснет до того, как контакт введён, и причина написана
 * рядом с ней, а не узнаётся отказом после нажатия.
 *
 * Проверки дублей до сохранения у лида нет: сервер ищет похожие лиды только при заведении
 * (`possibleDuplicates` в ответе), отдельного запроса «есть ли такой» у него нет. Блок
 * «телефон совпадает с лидом…» из макета поэтому не рисуется — пробел API, а не забытая
 * разметка. Ответственным становится тот, кто заводит (#101), — это сказано подсказкой.
 */
export function LeadCreatePage() {
  const { t, user } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // A call from an unknown number (#122) opens this page with the number filled in.
  const [params] = useSearchParams()
  const [form, setForm] = useState<NewLead>(() => {
    const phone = params.get('phone')
    return phone ? { title: '', source: 'call', phone } : { title: '', source: 'call' }
  })
  const [notice, setNotice] = useState<string | null>(null)
  const set = (field: keyof NewLead, value: string) => setForm((previous) => ({ ...previous, [field]: value }))

  const save = useMutation({
    mutationFn: () => createLead(blankToNull(form)),
    onSuccess: (intake) => {
      void queryClient.invalidateQueries({ queryKey: ['list', 'sales.lead'] })
      navigate(`/leads/${intake.lead.id}`)
    },
    onError: (error) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  if (!can(user, 'sales.lead', 'create')) return <AccessDenied />

  const hasContact = [form.phone, form.email, form.inn].some((value) => value && value.trim() !== '')
  const blockedReason = form.title.trim() === ''
    ? t('lead.create.needTitle')
    : !hasContact
      ? t('lead.create.needContact')
      : null

  const field = (id: keyof NewLead, label: string, options?: { span?: boolean; help?: string; mono?: boolean }) => (
    <CreateField id={`lead-${id}`} label={label} span={options?.span} help={options?.help}>
      <input
        id={`lead-${id}`}
        className={options?.mono ? 'input mono' : 'input'}
        value={(form[id] as string | undefined) ?? ''}
        onChange={(event) => set(id, event.target.value)}
      />
    </CreateField>
  )

  return (
    <CreateModal
      overline={t('nav.leads')}
      title={t('lead.create.title')}
      error={notice}
      blockedReason={blockedReason}
      pending={save.isPending}
      submitLabel={save.isPending ? t('create.saving') : t('lead.create.submit')}
      onCancel={() => navigate('/leads')}
      onSubmit={() => {
        setNotice(null)
        save.mutate()
      }}
    >
      <div className="form-grid">
        {field('companyName', t('column.company_name'))}
        {field('inn', t('column.inn'), { mono: true, help: t('lead.create.innHelp') })}
        {field('contactName', t('column.contact_name'), { span: true })}
        {field('phone', t('column.phone'))}
        {field('email', t('column.email'))}
        <CreateField id="lead-source" label={t('column.source')} required>
          <select id="lead-source" className="input" value={form.source} onChange={(event) => set('source', event.target.value)}>
            {SOURCES.map((source) => (
              <option key={source} value={source}>
                {t(`lead.source.${source}`)}
              </option>
            ))}
          </select>
        </CreateField>
        <CreateField label={t('column.responsible_employee_id')} help={t('lead.create.responsibleMe')}>
          <span className="input is-readonly">{t('lead.create.responsibleYou')}</span>
        </CreateField>
        <CreateField id="lead-title" label={t('lead.create.what')} required span>
          <textarea
            id="lead-title"
            className="input"
            value={form.title}
            required
            maxLength={512}
            onChange={(event) => set('title', event.target.value)}
          />
        </CreateField>
      </div>
    </CreateModal>
  )
}
